import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Chip,
  Divider,
  Breadcrumbs,
  Link,
  TextField,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Save as SaveIcon,
  FolderOpen as LoadIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Publish as PublishIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Settings as SettingsIcon,
  Share as ShareIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  GridView as GridViewIcon,
  Star as StarIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import FullscreenButton from '../FullscreenButton';

function FormBuilderHeader({
  formName,
  setFormName,
  formElements,
  isAutoSaving,
  undoStack,
  redoStack,
  previewMode,
  setPreviewMode,
  publishing,
  darkMode,
  showPropertiesPanel,
  setShowPropertiesPanel,
  onUndo,
  onRedo,
  onLoad,
  onSave,
  onPublish,
  onShare,
  onSettings, // Add this prop
  onFormSettings, // Add this prop
  onExport,
  onImport,
}) {
  const theme = useTheme();
  const compactActions = useMediaQuery(theme.breakpoints.down('md'));
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);

  // Handle form settings - use multiple prop names for compatibility
  const handleFormSettings = () => {
    if (onSettings) {
      onSettings();
    } else if (onFormSettings) {
      onFormSettings();
    } else if (onShare) {
      onShare(); // Fallback to share handler
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      {/* Top Navigation Bar */}
      <Box sx={{
        px: { xs: 2, sm: 3 },
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: 1,
        borderColor: 'divider',
      }}
      >
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Form Builder
          </Typography>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
            <Link
              underline="hover"
              color="inherit"
              href="/"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <HomeIcon fontSize="small" />
              Home
            </Link>
            <Typography color="text.primary">Forms</Typography>
            <Typography color="text.primary">Builder</Typography>
          </Breadcrumbs>

          {isAutoSaving && (
            <Chip
              icon={<SaveIcon fontSize="small" />}
              label="Auto-saving..."
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        {/* Right Section */}
        <Stack direction="row" spacing={1} alignItems="center">
          <FullscreenButton />

          <Tooltip title="Undo">
            <span>
              <IconButton
                onClick={onUndo}
                disabled={undoStack.length === 0}
                size="small"
              >
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Redo">
            <span>
              <IconButton
                onClick={onRedo}
                disabled={redoStack.length === 0}
                size="small"
              >
                <RedoIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <FormControl size="small" sx={{ minWidth: compactActions ? 96 : 120 }}>
            <InputLabel>Preview</InputLabel>
            <Select
              value={previewMode}
              onChange={(e) => setPreviewMode(e.target.value)}
              label="Preview"
            >
              <MenuItem value="desktop">Desktop</MenuItem>
              <MenuItem value="tablet">Tablet</MenuItem>
              <MenuItem value="mobile">Mobile</MenuItem>
            </Select>
          </FormControl>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {!compactActions && (
            <Button
              variant="outlined"
              startIcon={<LoadIcon />}
              onClick={onLoad}
              size="small"
            >
              Load
            </Button>
          )}

          {!compactActions && (
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={onSave}
              size="small"
            >
              Save
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={onPublish}
            disabled={publishing}
            size="small"
            sx={{ fontWeight: 600, minWidth: compactActions ? 36 : undefined, px: compactActions ? 1 : 1.5 }}
          >
            {compactActions ? '' : (publishing ? 'Publishing...' : 'Publish')}
          </Button>
          <IconButton
            size="small"
            aria-label="More form actions"
            onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Secondary Toolbar */}
      <Box sx={{
        px: { xs: 2, sm: 3 },
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'grey.50',
        ...(darkMode && { bgcolor: 'grey.900' }),
      }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            placeholder="Form Name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            size="small"
            variant="outlined"
            sx={{ minWidth: 200 }}
          />

          <Chip
            icon={<GridViewIcon fontSize="small" />}
            label={`${formElements.length} fields`}
            size="small"
            variant="outlined"
          />

          {formElements.length > 0 && (
            <Chip
              icon={<StarIcon fontSize="small" />}
              label="Draft"
              size="small"
              color="warning"
            />
          )}
        </Box>

        {!compactActions && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Properties Panel">
              <IconButton
                onClick={() => setShowPropertiesPanel(!showPropertiesPanel)}
                size="small"
                color={showPropertiesPanel ? 'primary' : 'default'}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Form Settings">
              <IconButton
                onClick={handleFormSettings}
                size="small"
              >
                <ShareIcon />
              </IconButton>
            </Tooltip>

            <Button
              variant="text"
              startIcon={<DownloadIcon />}
              onClick={onExport}
              size="small"
            >
              Export
            </Button>

            <Button
              variant="text"
              component="label"
              startIcon={<UploadIcon />}
              size="small"
            >
              Import
              <input
                type="file"
                accept=".json"
                onChange={onImport}
                style={{ display: 'none' }}
              />
            </Button>
          </Stack>
        )}
      </Box>
      <Menu
        anchorEl={actionsMenuAnchor}
        open={Boolean(actionsMenuAnchor)}
        onClose={() => setActionsMenuAnchor(null)}
      >
        <MenuItem onClick={() => { setActionsMenuAnchor(null); onLoad(); }}>
          <LoadIcon fontSize="small" style={{ marginRight: 8 }} />
          Load
        </MenuItem>
        <MenuItem onClick={() => { setActionsMenuAnchor(null); onSave(); }}>
          <SaveIcon fontSize="small" style={{ marginRight: 8 }} />
          Save
        </MenuItem>
        <MenuItem onClick={() => { setActionsMenuAnchor(null); handleFormSettings(); }}>
          <ShareIcon fontSize="small" style={{ marginRight: 8 }} />
          Form settings
        </MenuItem>
        <MenuItem onClick={() => { setActionsMenuAnchor(null); setShowPropertiesPanel(!showPropertiesPanel); }}>
          <SettingsIcon fontSize="small" style={{ marginRight: 8 }} />
          {showPropertiesPanel ? 'Hide properties panel' : 'Show properties panel'}
        </MenuItem>
        <MenuItem onClick={() => { setActionsMenuAnchor(null); onExport(); }}>
          <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
          Export
        </MenuItem>
        <MenuItem component="label">
          <UploadIcon fontSize="small" style={{ marginRight: 8 }} />
          Import
          <input
            type="file"
            accept=".json"
            onChange={(e) => {
              setActionsMenuAnchor(null);
              onImport(e);
            }}
            style={{ display: 'none' }}
          />
        </MenuItem>
      </Menu>
    </Paper>
  );
}

export default FormBuilderHeader;
